import React, { useMemo, useState } from 'react';
import ButtonSecondary from 'shared/Button/ButtonSecondary';
import CommonLayout from './CommonLayout';
import { useUserSessionStore } from '../../stores/UserSession.store';
import moment from 'moment';
import { orderBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useOrders } from '../../hooks/collections/useOrdersV2';
import { BadgeOrder } from '../../components/OrderStatus/BadgeOrder';
import Label from '../../components/Label/Label';
import Input from '../../shared/Input/Input';
import { OrderStatusEnum } from '../../enums/OrderStatus.enum';
import { OrderStatus } from '../../@core/constants/OrderStatus.constant';
import { NotFound } from '../../components/NotFound/NotFound';

const AccountCompanyOrders = () => {
  const navigate = useNavigate();
  const user = useUserSessionStore((state) => state.user);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OrderStatusEnum | 'all'>('all');
  const [startDate, setStartDate] = useState<string>(moment().startOf('year').format('YYYY-MM-DD'));
  const { orders } = useOrders({
    loadData: true,
    queries: [
      { type: 'where', field: 'service.company.id', operator: '==', value: user?.id || '' },
      { type: 'orderBy', field: 'createdAt', direction: 'desc' }
    ]
  });

  const ordersByUser = useMemo(() => {
    return (orders || [])
      .filter((order) => status !== 'all' ? order?.status.toString() === status.toString() : true)
      .filter((order) => search !== '' ? order?.orderId.startsWith(search) : true)
      .filter((order) => startDate !== '' ? moment(order?.createdAt).isSameOrAfter(startDate) : true);
  }, [orders, user?.id, status, search, startDate]);

  const renderSection1 = () => {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="flex flex-col md:flex-row gap-5 items-center justify-between">
          <h2 className="text-3xl font-semibold">Company orders</h2>
          <div className={'flex items-center space-x-2'}>
            <div>
              <Label>Order ID</Label>
              <Input
                onChange={(e) => setSearch(e.target.value)}
                className="mt-1.5 h-9"
                placeholder={'XXXX-XXXX-XXX-XXXX-XXX'}
              />
            </div>
            <div>
              <Label>Status</Label>
              <select
                onChange={(e) => e.target.value === 'all' ? setStatus('all') : setStatus(e.target.value as unknown as OrderStatusEnum)}
                className="mt-1.5 h-9"
              >
                <option value="all">All</option>
                <option value={OrderStatusEnum.PAID}>{OrderStatus[OrderStatusEnum.PAID].label}</option>
                <option value={OrderStatusEnum.CANCELLED}>{OrderStatus[OrderStatusEnum.CANCELLED].label}</option>
                <option value={OrderStatusEnum.GENERATED}>{OrderStatus[OrderStatusEnum.GENERATED].label}</option>
              </select>
            </div>
            <div className="max-w-lg">
              <Label>Orders since</Label>
              <Input
                className="mt-1.5 h-9"
                type="date"
                defaultValue={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div className={'overflow-x-auto'}>
          {orders.length ? (
            <table className={'w-full text-left min-w-[1000px] border-separate border-spacing-4'}>
              <thead>
              <tr>
                <th>Order ID</th>
                <th>Service</th>
                <th>Status</th>
                <th>Location</th>
                <th>Total</th>
              </tr>
              </thead>
              <tbody>
              {orderBy(ordersByUser, ['createdAt'], ['desc']).map((order: any) => (
                <tr key={order.id}>
                  <td>{order.orderId}</td>
                  <td>{order.service.title}</td>
                  <td><BadgeOrder status={order.status}/></td>
                  <td>{order.service.details.location.address}</td>
                  <td>${order.total}</td>
                  <td>
                    <ButtonSecondary onClick={() => navigate(`/order-detail/${order.id}`)}>View
                      Details</ButtonSecondary>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          ) : (
            <NotFound description={'No orders found for your company.'}/>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <CommonLayout>{renderSection1()}</CommonLayout>
    </div>
  );
};

export default AccountCompanyOrders;
